import { STATUS_ESTRUTURA_UI } from '@/ui';
import type { BarragemStatus } from '@/domain/estrutura';
import { ehBarragem, rotuloCurtoDaEstrutura, rotuloDaEstrutura } from '@/domain/estrutura/rotulo';

interface StructureChipsProps {
  /** Barragens visíveis com status calculado (do `useDamScope`). */
  dams: BarragemStatus[];
}

/**
 * Chips compactos de status por barragem (A/B/C) no header — GUPM-158.
 *
 * Espelha o `.hs-card` do protótipo v5.10 (docs/reference/GeoGuard_UPM_v5_10_0_8.html,
 * CSS L539-546 / `buildHeaderStructStatus` L1364): um card por barragem com
 * dot + código + nível — SEM chip "Geral" (o consolidado é lido dos próprios
 * chips; o pior fica visível). Puro (props); o Header injeta via `useDamScope`.
 *
 * Responsivo: o rótulo some abaixo de `lg` (fica só dot + código), mas os chips
 * NÃO somem — o grupo da direita do header é protegido (shrink-0) e quem cede é a
 * marca à esquerda. Prioridade pedida pelo dono: manter as pills sempre visíveis.
 *
 * Só BARRAGENS (GUPM-329): a Mina está em `dams` por causa do marco superficial, mas
 * não é barragem regulada e nunca sai de "Normal" (marco não tem Carta de Controle).
 * Decisão do dono (28/09/2026): fora do header; segue no filtro, Mapa, M6 e Dashboard.
 */
export function StructureChips({ dams }: StructureChipsProps) {
  return (
    <div role="group" aria-label="Status das barragens" className="flex items-center gap-1.5">
      {dams.filter((d) => ehBarragem(d.code)).map((d) => {
        const ui = STATUS_ESTRUTURA_UI[d.status.nivel];
        const desc = `${rotuloDaEstrutura(d.code) ?? d.code}: ${ui.label}${d.status.pre ? ' (pré-ANM)' : ''}`;
        return (
          <span
            key={d.id}
            data-status={d.status.nivel}
            aria-label={desc}
            title={desc}
            className="inline-flex items-center gap-1.5 px-2 min-[420px]:px-2.5 py-1 rounded-lg border-[1.5px] whitespace-nowrap"
            style={{ color: ui.color, backgroundColor: ui.bg, borderColor: ui.border }}
          >
            <span aria-hidden className="w-[7px] h-[7px] rounded-full shrink-0" style={{ background: ui.color }} />
            <span className="text-[11px] font-extrabold leading-none">{rotuloCurtoDaEstrutura(d.code) ?? d.code}</span>
            <span className="text-[10px] font-semibold opacity-90 leading-none hidden lg:inline">{ui.label}</span>
          </span>
        );
      })}
    </div>
  );
}
